import useAdminStore from '../store/adminStore';

export interface TeamMemberInfo {
    isTeam: boolean;
    role: string | null;
    category: string | null;
    id: string | null;
}

type TeamMemberLike = {
    id?: string;
    email?: string;
    role?: string;
    category?: string;
};

const normalize = (v: string | null | undefined): string =>
    String(v || '').trim().toLowerCase();

/** Zweites Netz: PreisExpert-Domains gelten als Team (case-insensitive). */
const TEAM_EMAIL_DOMAINS = ['@preisexpert.de', '@preisexpert.info'];

const isPreisExpertDomainEmail = (email?: string | null): boolean => {
    const e = normalize(email);
    if (!e.includes('@')) return false;
    return TEAM_EMAIL_DOMAINS.some((domain) => e.endsWith(domain));
};

/** Nur echte Kommandozentrale-Admins (E-Mail). */
const HARD_ADMIN_EMAILS = new Set([
    'erfolg@friseur-coaching.de', // Christian – Superadmin
    'coaching@preisexpert.info', // Martina – Admin
]);

export const isHardcodedAdminEmail = (email?: string | null): boolean =>
    HARD_ADMIN_EMAILS.has(normalize(email));

const isCoachOrSupportLabel = (value?: string | null): boolean => {
    const v = normalize(value);
    return v === 'coach' || v === 'support';
};

const isAdminLabel = (value?: string | null): boolean => {
    const v = normalize(value);
    return v === 'admin' || v === 'superadmin';
};

/**
 * Coach-Portal (Jean, Nelly) und Support-Portal (Omar):
 * gleiche Rechte, Support nur nicht zuordenbar.
 * Coach/Support schlagen fehlerhafte Admin-Reste immer.
 */
export const isCoachPortalAccess = (
    role?: string | null,
    category?: string | null,
    email?: string | null
): boolean => {
    if (isHardcodedAdminEmail(email)) return false;
    if (isCoachOrSupportLabel(category) || isCoachOrSupportLabel(role)) return true;
    return false;
};

/**
 * Kommandozentrale nur für Christian + Martina (bzw. explizit admin/superadmin
 * ohne Coach/Support-Kennzeichnung).
 * Jean / Nelly = Coach. Omar = Support. Niemals Admin.
 */
export const isAdminPortalAccess = (
    role?: string | null,
    category?: string | null,
    email?: string | null
): boolean => {
    if (isHardcodedAdminEmail(email)) return true;

    // Coach & Support niemals in die Kommandozentrale
    if (isCoachOrSupportLabel(category) || isCoachOrSupportLabel(role)) return false;

    return isAdminLabel(role) || isAdminLabel(category);
};

/** Effektive Portal-Rolle für Routing/UI. */
export const resolveEffectiveRole = (member: TeamMemberLike): string => {
    if (isHardcodedAdminEmail(member.email)) {
        const r = normalize(member.role);
        const c = normalize(member.category);
        return r === 'superadmin' || c === 'superadmin' ? 'superadmin' : 'admin';
    }

    // Coach / Support zuerst (Jean, Nelly, Omar)
    if (isCoachOrSupportLabel(member.category)) return normalize(member.category);
    if (isCoachOrSupportLabel(member.role)) return normalize(member.role);

    if (isAdminLabel(member.role)) return normalize(member.role);
    if (isAdminLabel(member.category)) return normalize(member.category);

    return 'coach';
};

export const resolveTeamMemberByEmail = (
    email: string | null | undefined,
    team: TeamMemberLike[] = [],
    teamRegistry: TeamMemberLike[] = []
): TeamMemberInfo => {
    if (!email) return { isTeam: false, role: null, category: null, id: null };

    const userEmail = normalize(email);
    if (userEmail === 'erfolg@friseur-coaching.de') {
        return { isTeam: true, role: 'superadmin', category: 'admin', id: null };
    }

    const pool = [...(team || []), ...(teamRegistry || [])];
    const storeMember = pool.find((m) => m.email && normalize(m.email) === userEmail);
    if (storeMember) {
        const effective = resolveEffectiveRole(storeMember);
        const portalCategory =
            effective === 'admin' || effective === 'superadmin'
                ? 'admin'
                : effective === 'support'
                    ? 'support'
                    : effective === 'coach'
                        ? 'coach'
                        : (storeMember.category ? normalize(storeMember.category) : null);
        return {
            isTeam: true,
            role: effective,
            category: portalCategory,
            id: storeMember.id ? String(storeMember.id) : null,
        };
    }

    if (isHardcodedAdminEmail(userEmail)) {
        return { isTeam: true, role: 'admin', category: 'admin', id: null };
    }

    const adminTeam = useAdminStore.getState().adminTeam || [];
    const adminMember = adminTeam.find((m) => m.email && normalize(m.email) === userEmail);
    if (adminMember) {
        const effective = resolveEffectiveRole(adminMember);
        return {
            isTeam: true,
            role: effective,
            category: effective === 'admin' || effective === 'superadmin'
                ? 'admin'
                : effective === 'support'
                    ? 'support'
                    : 'coach',
            id: adminMember.id ? String(adminMember.id) : null,
        };
    }

    // Domain-Fallback (Race / leerer Store): nie als Mandant behandeln
    if (isPreisExpertDomainEmail(userEmail)) {
        return { isTeam: true, role: 'coach', category: 'coach', id: null };
    }

    return { isTeam: false, role: null, category: null, id: null };
};

export const isTeamMemberEmail = (
    email: string | null | undefined,
    team: TeamMemberLike[] = [],
    teamRegistry: TeamMemberLike[] = []
): boolean => resolveTeamMemberByEmail(email, team, teamRegistry).isTeam;
